/**
 * Manage — a slide-over drawer above the app (not a separate page): banks,
 * recurring items and savings, each its own tab. Open from the dashboard's
 * Manage button; /manage deep-links to the same drawer over the dashboard.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useCallback, useEffect, useState } from 'react';
import { fmtR } from '../money.js';
import { taxPackAvailable } from '../global.js';
import { useSearchParams } from 'react-router-dom';
import AppNav from '../components/AppNav.jsx';
import { api } from '../api.js';
import { ask } from '../components/Ask.jsx';
import CategoryPicker from '../components/CategoryPicker.jsx';
import CadenceControls from '../components/CadenceControls.jsx';
import Dashboard from './Dashboard.jsx';
import VoiceButton from '../components/VoiceButton.jsx';
import SearchField from '../components/SearchField.jsx';
import { useAccordion } from '../components/AccordionRegister.jsx';

const TABS = [['Banks', '🏦'], ['Recurring', '🔁'], ['Savings', '🐷'], ['Cash', '💵'], ['Rules', '🧠'], ['Logged', '📋'], ['Tax', '🧾']];
// The Tax tab is South Africa only (SARS rules); other accounts never see it.
const visibleTabs = () => TABS.filter(([t]) => t !== 'Tax' || taxPackAvailable());

// One place to see EVERY logged item for the current budget month — spends
// logged at shops, unit ticks and settled trips — each deletable (reverses
// the money + units fully). Web-only, lives in the Manage drawer (#logged-view).
function Logged() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const load = useCallback(() => {
    api('/budget/place-visits/?budget=current')
      .then((vs) => setRows((Array.isArray(vs) ? vs : []).filter((v) => v.confirmed)))
      .catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    const ok = await ask({ title: 'Delete this logged item?',
      message: 'It reverses the money and units fully.', ok: 'Delete' });
    if (!ok) return;
    await api(`/budget/place-visits/${id}/`, { method: 'DELETE' }).catch(() => {});
    load();
  };
  if (rows === null) return <div className="text-sm text-slate-400">Loading…</div>;
  const total = rows.reduce((sum, v) => sum + (Number(v.amount) || 0), 0);
  const ql = q.trim().toLowerCase();
  const shown = ql ? rows.filter((v) => `${v.item_title || ''} ${v.place_name || ''} ${v.bank_name || ''}`.toLowerCase().includes(ql)) : rows;
  const shownTotal = shown.reduce((sum, v) => sum + (Number(v.amount) || 0), 0);
  const sorted = shown.slice().sort((a, b) => String(b.on).localeCompare(String(a.on)));
  return (
    <div data-guide="manage-logged">
      {/* Four-card summary strip (Pass 7): the question you arrived with. */}
      {(() => {
        const visits = rows.filter((v) => (Number(v.value != null ? v.value : v.amount) || 0) === 0).length;
        const reversals = rows.filter((v) => Number(v.amount) < 0 || Number(v.units) < 0).length;
        const cards = [
          ['Logged', String(rows.length), 'this month'],
          ['Total', fmtR(total), 'value logged'],
          ['Free visits', String(visits), 'R0 — still count'],
          ['Reversals', String(reversals), 'undone lines'],
        ];
        return (
          <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))' }}>
            {cards.map(([k, v, sub]) => (
              <div key={k} className="rounded-xl border border-edge bg-white/[0.03] px-4 py-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
                <div className="text-lg font-bold text-slate-100">{v}</div>
                <div className="text-[10px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>
        );
      })()}
      <div className="mb-3">
        <SearchField value={q} onChange={setQ} placeholder="Search by expense, place or bank…" />
      </div>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-bold">Logged this month</h3>
        <span className="text-sm text-slate-400">{shown.length} item{shown.length === 1 ? '' : 's'} · {fmtR(shownTotal)}</span>
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-slate-500">{ql ? 'No logged items match your search.' : 'Nothing logged yet this month. Every spend you log at a shop, unit tick and settled trip shows up here.'}</p>
      ) : (() => {
        const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const fmtDay = (iso) => {
          const q = String(iso || '').slice(0, 10).split('-');
          return q.length === 3 ? `${Number(q[2])} ${MON[Number(q[1]) - 1] || ''}` : String(iso || '');
        };
        const tagOf = (v) => (Number(v.amount) < 0 || Number(v.units) < 0) ? ['REVERSAL', 'text-red-400 border-red-400/50']
          : Number(v.units) > 0 ? ['UNIT', 'text-amber-400 border-amber-400/50']
          : Number(v.amount) > 0 ? ['SPEND', 'text-slate-300 border-edge']
          : ['VISIT', 'text-vpurple border-vpurple/50'];
        const byDay = {};
        for (const v of sorted) (byDay[String(v.on).slice(0, 10)] = byDay[String(v.on).slice(0, 10)] || []).push(v);
        const days = Object.keys(byDay).sort((a, b) => b.localeCompare(a));
        return (
          <div className="grid gap-4">
            {days.map((d) => (
              <div key={d}>
                <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">{fmtDay(d)} · {byDay[d].length}</h4>
                <div className="grid gap-2">
                  {byDay[d].map((v) => {
                    const [tag, cls] = tagOf(v);
                    const val = Number(v.value != null ? v.value : v.amount) || 0;
                    const free = val === 0;
                    return (
                      <div key={v.id} className="flex items-center gap-3 rounded-xl border border-edge p-3">
                        <span className={`text-[9px] uppercase tracking-wide border rounded px-1.5 py-0.5 shrink-0 ${cls}`}>{tag}</span>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold truncate">{v.item_title || v.place_name || 'Logged item'}</div>
                          <div className="text-xs text-slate-400 truncate">
                            {v.place_name || ''}{v.bank_name ? ` · ${v.bank_name}` : ''}{Number(v.units) ? ` · ${v.units} units` : ''}
                            {tag === 'REVERSAL' ? ' · deleting re-applies the original spend' : ''}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`font-semibold ${free ? 'text-slate-600' : ''}`}>{fmtR(val)}</div>
                          <button className="text-xs text-slate-500 hover:text-red-400" onClick={() => del(v.id)}>Delete</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

/** The drawer itself — mount anywhere with an onClose. */
export function ManageDrawer({ onClose, initialTab }) {
  const [tab, setTab] = useState(initialTab || 'Banks');
  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />
      <div className="absolute right-0 top-0 h-full w-full max-w-3xl bg-panel border-l border-edge
                      shadow-2xl overflow-y-auto animate-[slideIn_.2s_ease-out]"
           style={{ animationName: 'none' }}
           data-guide-context="manage"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-panel/95 backdrop-blur border-b border-edge px-6 py-4
                        flex items-center gap-4 z-10">
          <img src="/logo.svg" alt="" className="w-7 h-7" />
          <h1 className="font-bold text-lg flex-1">Manage</h1>
          <button className="w-9 h-9 rounded-full border border-edge hover:border-slate-400
                             text-slate-400 hover:text-white transition" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="px-6 pt-4 flex gap-2" data-guide="manage-tabs">
          {visibleTabs().map(([t, icon]) => (
            <button key={t}
                    data-guide={t === 'Recurring' ? 'tab-recurring' : t === 'Logged' ? 'tab-logged' : undefined}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition ${
                      tab === t ? 'border-vpurple bg-vpurple/15' : 'border-edge text-slate-400 hover:border-slate-500'}`}
                    onClick={() => setTab(t)}>
              {icon} {t}
            </button>
          ))}
        </div>
        <div className="px-6 py-5">
          {tab === 'Banks' && <Banks />}
          {tab === 'Recurring' && <Recurring />}
          {tab === 'Savings' && <Savings />}
          {tab === 'Rules' && <Rules />}
          {tab === 'Logged' && <Logged />}
          {tab === 'Cash' && <MoneySpots />}
          {tab === 'Tax' && taxPackAvailable() && <TaxPack />}
        </div>
      </div>
    </div>
  );
}

/** /manage deep-link: the dashboard with the drawer already open. */
export default function ManagePage() {
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get('tab') || 'Banks');
  return (
    <div className="min-h-screen bg-ink text-slate-100">
      <AppNav active="manage" />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
        <h1 className="text-xl font-bold mb-4">Manage</h1>
        <div className="flex gap-2 flex-wrap mb-6" data-guide="manage-tabs">
          {visibleTabs().map(([t, icon]) => (
            <button key={t}
                    data-guide={t === 'Recurring' ? 'tab-recurring' : t === 'Logged' ? 'tab-logged' : undefined}
                    className={`px-3.5 py-2 rounded-xl border text-sm font-medium transition ${
                      tab === t ? 'border-vpurple bg-vpurple/15 text-white'
                                : 'border-edge text-slate-400 hover:border-slate-500'}`}
                    onClick={() => setTab(t)}>
              {icon} {t}
            </button>
          ))}
        </div>
        <div>
          {tab === 'Banks' && <Banks />}
          {tab === 'Recurring' && <Recurring />}
          {tab === 'Savings' && <Savings />}
          {tab === 'Rules' && <Rules />}
          {tab === 'Logged' && <Logged />}
          {tab === 'Cash' && <MoneySpots />}
          {tab === 'Tax' && taxPackAvailable() && <TaxPack />}
        </div>
      </div>
    </div>
  );
}

function Banks() {
  const acc = useAccordion();   // shared open-row register — banks are single-open, cross-section
  const [banks, setBanks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [name, setName] = useState('');
  const [editing, setEditing] = useState(null);   // id being renamed inline
  const [editVal, setEditVal] = useState('');
  const [acctName, setAcctName] = useState('');   // add-form optional account nickname
  const [editAcct, setEditAcct] = useState('');   // nickname while renaming a bank
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState('');
  const [chip, setChip] = useState('');
  const [monthId, setMonthId] = useState(null);   // current month id — for Set balance (set_bank)
  const load = useCallback(() => {
    api('/budget/banks/').then(setBanks);
    api('/budget/settings/').then(setSettings).catch(() => {});
    (async () => {
      try {
        const cur = await api('/budget/months/current/');
        const b = await api(`/budget/months/ensure/?month=${cur.month}`);
        setMonthId(b.id);
        setSummary(await api(`/budget/months/${b.id}/summary/`));
      } catch { /* summary strip is best-effort */ }
    })();
  }, []);
  useEffect(() => { load(); }, [load]);
  // The bank ROLES — receiving (master income lands here) and savings home
  // (the pot lives here) — are choices, changeable any time for this month
  // and the months ahead.
  const setRole = async (b, field) => {
    await api('/budget/settings/', { method: 'PATCH', body: { [field]: b.id } });
    load();
  };
  // Payday: the expected salary day + whether the month rolls on a fixed day or
  // only once the salary is ticked received. This drives the payday hand-off and
  // the roll-over offer, so it is set here (and at registration).
  const saveSetting = async (field, value) => {
    setSettings((s) => ({ ...s, [field]: value }));
    await api('/budget/settings/', { method: 'PATCH', body: { [field]: value } });
    load();
  };
  const add = async () => {
    if (!name.trim()) return;
    await api('/budget/banks/', { method: 'POST',
      body: { name: name.trim(), account_name: acctName.trim() } });
    setName(''); setAcctName(''); load();
  };
  const saveRename = async (b) => {
    const nm = editVal.trim(), acct = editAcct.trim();
    if (nm && (nm !== b.name || acct !== (b.account_name || ''))) {
      await api(`/budget/banks/${b.id}/`, { method: 'PATCH',
        body: { name: nm, account_name: acct } });
    }
    setEditing(null); load();
  };
  const toggleArchive = async (b) => {
    await api(`/budget/banks/${b.id}/`, { method: 'PATCH', body: { archived: !b.archived } });
    load();
  };
  // Set balance — the actual money in this bank right now (Pass 5, parity with
  // the dashboard's eight bank actions). Writes the month's per-bank `remaining`
  // (actual-now) via set_bank, so the bank enters the real leftover total.
  const setBalance = async (b) => {
    if (!monthId) return;
    const pb = (summary?.per_bank || []).find((x) => String(x.bank) === String(b.id));
    const v = await ask({ title: `Set ${b.account_name ? `${b.name} \u00b7 ${b.account_name}` : b.name} balance`, message: 'What is actually in this bank right now?', input: true, value: pb && pb.has_actual ? pb.actual : '', ok: 'Set balance' });
    if (v !== null && v !== undefined && String(v).trim() !== '') {
      await api(`/budget/months/${monthId}/set_bank/`, { method: 'POST', body: { bank: b.id, remaining: String(v).replace(',', '.') } });
      load();
    }
  };
  return (
    <div>
      {/* Four-card summary strip (mockup): the question you arrived with. */}
      {(() => {
        const pbs = (summary && summary.per_bank) || [];
        const active = banks.filter((b) => !b.archived);
        const bal = pbs.filter((x) => x.has_actual).reduce((a, x) => a + Number(x.actual || 0), 0);
        const stp = pbs.reduce((a, x) => a + Number(x.left_to_pay || 0), 0);
        const cards = [
          ['Banks', String(active.length), 'all active'],
          ['Balance now', fmtR(bal), `across ${active.length} bank${active.length === 1 ? '' : 's'}`],
          ['Still to pay', fmtR(stp), 'this cycle'],
          ['Real leftover', summary ? fmtR(summary.leftover_actual) : '—', 'genuinely free'],
        ];
        return (
          <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))' }}>
            {cards.map(([k, v, sub]) => (
              <div key={k} className="rounded-xl border border-edge bg-white/[0.03] px-4 py-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
                <div className="text-lg font-bold text-slate-100">{v}</div>
                <div className="text-[10px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>
        );
      })()}
      <div className="mb-2">
        <SearchField value={q} onChange={setQ} placeholder="Search your banks…"
          aiLine={q.trim() ? `${banks.filter((b) => (b.name || '').toLowerCase().includes(q.trim().toLowerCase())).length} match${banks.filter((b) => (b.name || '').toLowerCase().includes(q.trim().toLowerCase())).length === 1 ? '' : 'es'}` : ''} />
      </div>
      <div className="flex gap-1.5 flex-wrap mb-3">
        {[['red', 'In the red'], ['archived', 'Archived']].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setChip(chip === k ? '' : k)}
                  className={`text-xs px-3 min-h-[32px] rounded-full border transition ${chip === k ? 'border-vpurple text-vpurple bg-vpurple/10' : 'border-edge text-slate-500 hover:border-slate-400'}`}>{label}</button>
        ))}
      </div>
      <div className="grid gap-2">
        {banks
          .filter((b) => {
            const pb = (summary && summary.per_bank || []).find((x) => x.bank === b.id);
            const red = !!(pb && pb.profit != null && Number(pb.profit) < 0);
            if (chip === 'archived') return b.archived;
            if (chip === 'red') return !b.archived && red;
            return !b.archived;
          })
          .filter((b) => !q.trim() || `${b.name || ''} ${b.account_name || ''}`.toLowerCase().includes(q.trim().toLowerCase()))
          .map((b) => {
          const pb = (summary && summary.per_bank || []).find((x) => x.bank === b.id);
          const short = !!pb && pb.has_actual && Number(pb.profit || 0) < 0;
          return (
          <div key={b.id}
               className={`border rounded-xl ${b.archived ? 'border-edge/50 opacity-50' : short ? 'border-amber-400/50' : 'border-edge'}`}>
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="text-lg">🏦</span>
            {editing === b.id ? (
              <div className="flex-1 flex gap-1.5 items-center">
                <input autoFocus className="input !py-1 flex-1" value={editVal}
                       placeholder="Bank name"
                       onChange={(e) => setEditVal(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') saveRename(b); if (e.key === 'Escape') setEditing(null); }} />
                <input className="input !py-1 flex-1" value={editAcct}
                       placeholder="Account name (optional)"
                       onChange={(e) => setEditAcct(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') saveRename(b); if (e.key === 'Escape') setEditing(null); }} />
                <button className="text-xs text-vblue hover:text-white shrink-0"
                        onClick={() => saveRename(b)}>Save</button>
              </div>
            ) : (
              <button className={`flex-1 text-left font-medium ${b.archived ? 'line-through' : ''}`}
                      title="Click to rename"
                      onClick={() => { setEditing(b.id); setEditVal(b.name); setEditAcct(b.account_name || ''); }}>
                {b.account_name ? `${b.name} \u00b7 ${b.account_name}` : b.name}
              </button>
            )}
            {short && <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border text-amber-400 border-amber-400/50 shrink-0">SHORT</span>}
            {!b.archived && settings && (
              <>
                <button className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border ${
                          settings.primary_bank === b.id
                            ? 'border-vblue text-vblue'
                            : 'border-edge text-slate-600 hover:border-vblue'}`}
                        title="Receiving — the master income lands here; the payday hand-off funds the others from it"
                        onClick={() => settings.primary_bank !== b.id
                          && setRole(b, 'primary_bank')}>
                  {settings.primary_bank === b.id ? 'receiving ✓' : 'receiving?'}
                </button>
                <button className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border ${
                          settings.savings_bank === b.id
                            ? 'border-vpurple text-vpurple'
                            : 'border-edge text-slate-600 hover:border-vpurple'}`}
                        title="Savings home — the 🐷 pot lives here; deposits leave it, withdrawals return to it"
                        onClick={() => settings.savings_bank !== b.id
                          && setRole(b, 'savings_bank')}>
                  {settings.savings_bank === b.id ? '🐷 savings ✓' : 'savings?'}
                </button>
              </>
            )}
            {!b.archived && pb && (
              <button className="text-slate-500 hover:text-white text-sm w-5 text-center"
                      title="Show this bank's money breakdown"
                      onClick={() => acc.toggle('bank:' + b.id)}>
                {acc.openKey === 'bank:' + b.id ? '▾' : '▸'}
              </button>
            )}
            {!b.archived && (
              <button className="text-xs text-vblue hover:text-white" title="Set what this bank actually holds now" onClick={() => setBalance(b)}>Set balance</button>
            )}
            <button className="text-xs text-slate-500 hover:text-white" onClick={() => toggleArchive(b)}>
              {b.archived ? 'Restore' : 'Archive'}
            </button>
          </div>
          {acc.openKey === 'bank:' + b.id && pb && (() => {
            const prof = Number(pb.profit || 0);
            const cells = [
              ['Balance now', pb.has_actual ? fmtR(pb.actual) : 'not set', pb.has_actual ? '' : 'text-slate-500'],
              ['Income received', fmtR(pb.received_income || 0), ''],
              ['Still coming', fmtR(pb.income_coming || 0), ''],
              ['Still to pay', fmtR(pb.left_to_pay || 0), ''],
              ['Leftover', fmtR(prof), prof < 0 ? 'text-red-400' : 'text-emerald-400'],
              ['Funding', prof < 0 ? `Short ${fmtR(-prof)}` : 'Fully funded', prof < 0 ? 'text-amber-400' : 'text-emerald-400'],
            ];
            return (
              <div className="border-t border-edge/60 px-4 py-3 grid gap-3"
                   style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))' }}>
                {cells.map(([label, val, cls]) => (
                  <div key={label}>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
                    <div className={`text-sm font-semibold ${cls}`}>{val}</div>
                  </div>
                ))}
              </div>
            );
          })()}
          </div>
          );
          })}
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        <input className="input flex-1 min-w-[140px]" placeholder="Add a bank…" value={name}
               onChange={(e) => setName(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && add()} />
        <input data-guide="bank-add-account" className="input flex-1 min-w-[140px]" placeholder="Account name (optional)" value={acctName}
               onChange={(e) => setAcctName(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && add()} />
        <button className="btn-primary" onClick={add}>Add</button>
      </div>
      <p className="text-[11px] text-slate-500 mt-1">
        Add the same bank twice with different account names to track more than one account there.
      </p>
      {settings && (
        <div className="mt-6 border border-edge rounded-xl p-4">
          <h3 className="font-semibold text-sm">Payday</h3>
          <p className="text-xs text-slate-500 mt-1">
            When your month rolls over - this is what the payday hand-off and the
            roll-over offer use. You set it when you register; change it any time here.
          </p>
          <div className="mt-3 flex gap-2">
            {[['salary', 'When my salary lands'], ['day', 'A fixed day each month']].map(([k, label]) => (
              <button key={k}
                      className={`flex-1 text-xs py-2 rounded-xl border ${
                        settings.cycle_mode === k ? 'border-vblue bg-vblue/15 text-white' : 'border-edge text-slate-500'}`}
                      onClick={() => settings.cycle_mode !== k && saveSetting('cycle_mode', k)}>
                {label}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <label className="block text-xs text-slate-500 mb-1">
              Expected payday: day {settings.cycle_start_day || 25} of the month
            </label>
            <input type="range" min="1" max="28" value={settings.cycle_start_day || 25}
                   className="w-full"
                   onChange={(e) => setSettings((s) => ({ ...s, cycle_start_day: Number(e.target.value) }))}
                   onMouseUp={(e) => saveSetting('cycle_start_day', Number(e.target.value))}
                   onTouchEnd={(e) => saveSetting('cycle_start_day', Number(e.target.value))} />
            <p className="text-[11px] text-slate-600 mt-1">
              {settings.cycle_mode === 'salary'
                ? 'On this day Vault expects your salary and offers the roll-over; the new month starts when you tick it received.'
                : 'The new month opens on this day automatically.'}
            </p>
          </div>
        </div>
      )}
      <p className="text-xs text-slate-600 mt-3">
        Click a name to rename. Archiving hides a bank from new months — history stays.
      </p>
    </div>
  );
}

// The Manage · Tax tab is a READINESS CHECK, not one button (Pass 7): the three
// things the pack needs — deductible items, the travel logbook, the required
// certificates — each with its state and an amber tag on whatever is short, so
// the pack can be audited at a glance before opening the full /tax page. Every
// figure comes straight off /budget/tax-pack/ — nothing invented.
function TaxPack() {
  const acc = useAccordion();   // shared register — one tax row open at a time, product-wide
  const [pack, setPack] = useState(null);
  const [err, setErr] = useState('');
  // SA tax year runs 1 Mar–28 Feb; before March the current year still ends this
  // calendar year, otherwise it ends next year (matches the /tax page).
  const now = new Date();
  const year = now.getMonth() <= 1 ? now.getFullYear() : now.getFullYear() + 1;
  useEffect(() => {
    let live = true;
    api(`/budget/tax-pack/?year=${year}`)
      .then((d) => { if (live) setPack(d); })
      .catch((e) => { if (live) setErr(e?.data?.detail || e?.message || 'Could not load your tax pack.'); });
    return () => { live = false; };
  }, [year]);

  if (err) return <p className="text-red-400 text-sm" data-guide="manage-tax">{err}</p>;
  if (!pack) return <div className="text-sm text-slate-400" data-guide="manage-tax">Loading…</div>;

  const items = (pack.buckets || []).flatMap((b) => b.items || []);
  const nItems = items.length;
  const nBuckets = (pack.buckets || []).length;
  const noReceipt = items.filter((it) => !it.receipt).length;
  const trips = pack.travel_logbook || [];
  const heldTrips = trips.filter((t) => !t.client).length;
  const namedTrips = trips.length - heldTrips;
  const missingCerts = pack.missing_certificates || [];
  const certsIn = 3 - missingCerts.length;
  // Readiness percentage — only the checks that actually apply count, so an
  // empty year does not read as forever incomplete (same rule as /tax).
  const checks = [
    { ok: nItems > 0, applies: true },
    { ok: noReceipt === 0, applies: nItems > 0 },
    { ok: heldTrips === 0, applies: trips.length > 0 },
    { ok: missingCerts.length === 0, applies: true },
  ].filter((c) => c.applies);
  const readyPct = checks.length ? Math.round((100 * checks.filter((c) => c.ok).length) / checks.length) : 0;

  const cards = [
    ['Deductible', fmtR(pack.total_deductible), nItems ? `${nItems} item${nItems === 1 ? '' : 's'}` : 'nothing flagged yet'],
    ['Claimable travel', `${pack.travel_total_km || 0} km`, trips.length ? `${namedTrips} of ${trips.length} named` : 'no trips logged'],
    ['Certificates', `${certsIn} of 3`, 'all three required'],
    ['Pack readiness', `${readyPct}%`, readyPct === 100 ? 'ready to send' : 'not ready to send'],
  ];

  // The three readiness rows — each an accordion row (strict, one open at a time).
  const BUCKET_LABELS = { travel: 'Travel', home_office: 'Home office', donations: 'Donations',
    medical: 'Medical', retirement: 'Retirement', business: 'Business' };
  const rows = [
    { key: 'tax:deduct', icon: '🧾', title: 'Flagged tax-deductible',
      sub: nItems ? `${nItems} item${nItems === 1 ? '' : 's'} across ${nBuckets} SARS bucket${nBuckets === 1 ? '' : 's'}` : 'nothing flagged for this year yet',
      amt: fmtR(pack.total_deductible), warn: nItems === 0,
      tag: nItems === 0 ? 'nothing yet' : null,
      panel: (
        <div className="grid gap-1.5">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">By bucket</div>
          {(pack.buckets || []).length === 0 ? (
            <p className="text-xs text-slate-500">Open any expense, flag it tax-deductible on its Tax fields, and it lands here.</p>
          ) : (pack.buckets || []).map((b) => (
            <div key={b.bucket} className="flex items-center gap-2 text-sm">
              <span className="flex-1 min-w-0 truncate">{BUCKET_LABELS[b.bucket] || b.bucket}
                <span className="text-slate-500 text-xs"> · {(b.items || []).length} item{(b.items || []).length === 1 ? '' : 's'}</span>
              </span>
              <span className="font-medium shrink-0">{fmtR(b.total)}</span>
            </div>
          ))}
        </div>
      ) },
    { key: 'tax:travel', icon: '🚗', title: 'Travel logbook',
      sub: trips.length ? `${trips.length} tracked trip${trips.length === 1 ? '' : 's'} · ${namedTrips} with a client named` : 'no business trips logged yet',
      amt: `${pack.travel_total_km || 0} km`, warn: heldTrips > 0,
      tag: heldTrips > 0 ? `${heldTrips} unnamed` : null,
      panel: (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Business travel only</div>
          <p className="text-xs text-slate-400 leading-relaxed">
            {heldTrips > 0
              ? `${heldTrips} trip${heldTrips === 1 ? '' : 's'} ${heldTrips === 1 ? 'has' : 'have'} no client name and ${heldTrips === 1 ? 'is' : 'are'} held back from the pack. A trip needs a date, kilometres and a client to count.`
              : 'Every logged trip names a client — all of it is in the pack.'}
          </p>
        </div>
      ) },
    { key: 'tax:certs', icon: '📄', title: 'Certificates',
      sub: missingCerts.length ? `${certsIn} attached · ${missingCerts.length} still missing` : 'all three attached',
      amt: `${certsIn} of 3`, warn: missingCerts.length > 0,
      tag: missingCerts.length > 0 ? 'required' : null,
      panel: (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">What SARS requires</div>
          <p className="text-xs text-slate-400 leading-relaxed">
            Medical Aid, IT3(b) and RA certificates are required — the pack will not export as complete without all three.
            {missingCerts.length > 0 && ' Upload the missing ones on the tax pack.'}
          </p>
        </div>
      ) },
  ];

  return (
    <div data-guide="manage-tax">
      <p className="text-sm text-slate-400 mb-3">
        The three things the tax pack needs — deductible items, your travel logbook
        and the required certificates — and whether each is ready. Everything lands
        here when you flag it; open the full pack to print, export or upload.
      </p>

      {/* Four-card summary strip (Pass 7) — grid auto-fit, no media query. */}
      <div className="grid gap-2 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))' }}>
        {cards.map(([k, v, sub]) => (
          <div key={k} className={`rounded-xl border px-4 py-3 ${k === 'Pack readiness' && readyPct < 100 ? 'border-amber-400/50 bg-amber-400/[0.04]' : 'border-edge bg-white/[0.03]'}`}>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
            <div className={`text-lg font-bold ${k === 'Pack readiness' ? (readyPct === 100 ? 'text-emerald-400' : 'text-amber-400') : 'text-slate-100'}`}>{v}</div>
            <div className="text-[10px] text-slate-500">{sub}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-2">
        {rows.map((r) => {
          const open = acc.openKey === r.key;
          return (
            <div key={r.key} className={`rounded-xl border transition ${open ? 'border-vpurple' : r.warn ? 'border-amber-400/60 bg-amber-400/[0.04]' : 'border-edge'}`}>
              <button type="button" onClick={() => acc.toggle(r.key)}
                      className="flex items-center gap-3 w-full px-4 py-3 text-left min-h-[56px]"
                      title="Click to open — one row at a time">
                <span className="w-9 h-9 shrink-0 rounded-lg grid place-items-center text-[15px] bg-white/5">{r.icon}</span>
                <span className="flex-1 min-w-0">
                  <span className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                    <span className="truncate">{r.title}</span>
                    {r.tag && <span className="text-[9px] uppercase tracking-wide text-amber-400 border border-amber-400/60 rounded px-1.5 py-0.5">{r.tag}</span>}
                  </span>
                  <span className="text-[11px] text-slate-500 block truncate mt-0.5">{r.sub}</span>
                </span>
                <span className="text-sm font-semibold whitespace-nowrap">{r.amt}</span>
                <span className={`text-slate-500 text-xs shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
              </button>
              {open && <div className="px-4 pb-4">{r.panel}</div>}
            </div>
          );
        })}
      </div>

      <a href="/tax"
         className="inline-block mt-4 border border-vblue/50 bg-vblue/10 text-vblue rounded-xl px-4 py-2.5
                    hover:bg-vblue/15 transition text-sm font-medium min-h-[44px]">
        Open the tax pack — print, export CSV or upload certificates
      </a>
    </div>
  );
}

function Recurring() {
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const EMPTY_FORM = { kind: 'expense', title: '', amount: '', bank: '', from_bank: '',
    cadence: 'none', unit_rate: '', days_of_week: '', times_per_day: 1, expected_day: '', spend_only: false };
  const [form, setForm] = useState(EMPTY_FORM);
  const [aiBusy, setAiBusy] = useState(null);       // id with AI redo running
  const [aiMsg, setAiMsg] = useState('');
  const [curMonth, setCurMonth] = useState('');
  const [monthItems, setMonthItems] = useState([]);   // the CURRENT month's lines
  const [places, setPlaces] = useState([]);           // saved Spend-Map places (#299)
  const load = useCallback(() => {
    api('/budget/recurring/').then(setItems);
    api('/budget/banks/').then(setBanks);
    api('/budget/categories/').then(setCategories);
    api('/budget/places/').then(setPlaces).catch(() => {});
    api('/budget/months/current/').then((d) => {
      setCurMonth(d.month);
      return api(`/budget/months/ensure/?month=${d.month}`);
    }).then((b) => setMonthItems(b?.items || [])).catch(() => {});
  }, []);
  const onThisMonth = (it) => monthItems.some((m) =>
    String(m.template) === String(it.id)
    || (m.kind === it.kind && (m.title || '').toLowerCase() === (it.title || '').toLowerCase()));
  useEffect(() => { load(); }, [load]);

  const [pendingEdit, setPendingEdit] = useState(null); // {it, body, label}
  const [clearingAll, setClearingAll] = useState(false); // delete-ALL confirm
  const [merging, setMerging] = useState(null);   // item picking a merge partner
  const [q, setQ] = useState('');                 // search-as-you-type filter
  // The AI read of the search (#300): {filters, text} from parse-query, or null
  // while the plain substring pass is answering on its own.
  const [aiQ, setAiQ] = useState(null);
  const [testOff, setTestOff] = useState(() => new Set()); // temp-disabled expenses (what-if)
  // Accordion: exactly one row open at a time PRODUCT-WIDE and across sections
  // (opening a bank closes an open recurring row and vice-versa). Banks use
  // 'bank:<id>', recurring/manage rows use 'rec:<id>' on the shared register.
  const acc = useAccordion();
  const openId = acc.openKey && acc.openKey.startsWith('rec:') ? Number(acc.openKey.slice(4)) : null;
  const [cadDraft, setCadDraft] = useState(null);
  const [cadAdvOpen, setCadAdvOpen] = useState(false);   // "Repeat · advanced" disclosure
  // Filter chips (Income / Formula / Spend-only / Needs a name) — additive.
  const [filters, setFilters] = useState(() => new Set());
  const toggleFilter = (k) => setFilters((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const openRow = (it) => {
    const key = 'rec:' + it.id;
    const wasOpen = acc.openKey === key;
    acc.toggle(key);
    if (!wasOpen) {
      setCadDraft({ cadence: it.cadence || 'none', unit_rate: it.unit_rate ?? '',
        days_of_week: it.days_of_week || '', times_per_day: it.times_per_day || 1,
        expected_day: it.expected_day ?? '' });
      setCadAdvOpen(false);   // advanced repeat starts collapsed, per the mockup
    }
  };
  const askPatch = (it, body, label) => setPendingEdit({ it, body, label });
  const commitEdit = async (alsoThisMonth) => {
    const { it, body } = pendingEdit;
    setPendingEdit(null);
    await api(`/budget/recurring/${it.id}/`, { method: 'PATCH',
      body: alsoThisMonth ? { ...body, apply_month: curMonth } : body });
    load();
  };
  const monthLabel = (m) => {
    const [y, mo] = (m || '').split('-').map(Number);
    return mo ? new Date(y, mo - 1, 1).toLocaleString('en-ZA', { month: 'long' }) : 'this month';
  };
  const saveRename = (it, value) => {
    const v = (value || '').trim();
    if (v && v !== it.title) askPatch(it, { title: v }, `Rename to “${v}”`);
  };

  const redoAI = async (it) => {
    setAiBusy(it.id); setAiMsg('');
    const body = {}; const done = [];
    try {
      const d = await api('/budget/items/assist/',
                          { method: 'POST', body: { title: it.title, kind: it.kind } });
      if (d.category) { body.category = d.category; done.push(`category → ${d.category_name}`); }
    } catch { /* hint only */ }
    if (it.kind === 'expense') {
      try {
        const r = await api('/budget/places/resolve/',
                            { method: 'POST', body: { title: it.title } });
        const c = r.candidates?.[0];
        if (c) {
          const existing = await api('/budget/places/');
          let place = existing.find((p) => p.name.toLowerCase() ===
                                     r.query.split(',')[0].toLowerCase());
          if (!place) {
            place = await api('/budget/places/', { method: 'POST',
              body: { name: r.query.split(',')[0].slice(0, 60),
                      address: c.address, lat: c.lat, lng: c.lng } });
          }
          body.place = place.id; done.push(`map → ${place.name}`);
        }
      } catch { /* geocoding optional */ }
    }
    setAiBusy(null);
    if (done.length) askPatch(it, body, `✨ AI suggests: ${done.join(' · ')}`);
    else setAiMsg(`✨ ${it.title}: no confident suggestions — set them by hand.`);
  };
  const linkPlace = async (it, placeId) => {
    await api(`/budget/recurring/${it.id}/`, { method: 'PATCH',
      body: { place: placeId || null, apply_month: curMonth } });
    load();
    if (!placeId) return;
    const place = places.find((pp) => String(pp.id) === String(placeId));
    const yes = await ask({ title: 'Auto-log here?',
      message: `Make "${it.title}" a standing daily auto-log at ${place?.name || 'this place'}? `
        + 'You confirm each one when you are there.',
      ok: 'Yes, auto-log', cancel: 'Just link it' });
    if (yes) {
      const formula = !!(it.calculation && String(it.calculation).trim());
      try {
        await api('/budget/place-visit-rules/', { method: 'POST',
          body: { place: placeId, item_title: it.title,
                  units: formula ? 1 : 0, amount: formula ? 0 : (Number(it.amount) || 0),
                  cadence: 'daily', times_per_day: 1, active: true } });
        setAiMsg(`✓ "${it.title}" will auto-log daily at ${place?.name}.`);
      } catch { setAiMsg('Could not set the auto-log event.'); }
    }
  };
  const add = async () => {
    if (!form.title.trim()) return;
    const apiKind = form.kind === 'transfer' ? 'income' : form.kind;
    const v = (form.amount || '0').replace(/[x×]/gi, '*').trim();
    const body = { kind: apiKind, title: form.title, bank: form.bank || null };
    if (/[+*/-]/.test(v.slice(1))) { body.calculation = v; body.amount = '0'; }
    else { body.amount = v; }
    // A Transfer is stored as an income line carrying an explicit from_bank —
    // the source it moves OUT of. That single field is what makes it a move.
    if (apiKind === 'income') body.from_bank = form.from_bank || null;
    if (form.cadence && form.cadence !== 'none' && (form.kind !== 'expense' || !form.spend_only)) {
      body.cadence = form.cadence;
      body.times_per_day = Number(form.times_per_day) || 1;
      body.days_of_week = form.days_of_week || '';
      if (form.unit_rate !== '') body.unit_rate = form.unit_rate;
      if (form.expected_day !== '') body.expected_day = Number(form.expected_day);
    }
    // Ask where it starts: this month too (it joins this month's maths now),
    // or from next month only (this month is left exactly as it is).
    const when = await ask({ title: `Start "${form.title.trim()}" when?`,
      message: 'Add it to THIS month as well, so this month counts it now, or '
        + 'start it from next month and leave this month as it is?',
      ok: 'This month too', alt: 'From next month', cancel: 'Cancel' });
    if (!when) return;
    body.apply_current = when !== 'alt';
    await api('/budget/recurring/', { method: 'POST', body });
    setForm(EMPTY_FORM); load();
  };
  const patch = async (it, body, label) => {
    if (label) { askPatch(it, body, label); return; }
    await api(`/budget/recurring/${it.id}/`, { method: 'PATCH', body }); load();
  };
  const remove = async (it) => {
    if (await ask({ title: `Remove "${it.title}"?`,
                    message: 'It leaves your recurring setup and is removed from '
                      + 'FUTURE months — the current and past months keep their line.',
                    ok: 'Remove from future', danger: true, cancel: 'Keep it' })) {
      await api(`/budget/recurring/${it.id}/?scope=future`, { method: 'DELETE' }); load();
    }
  };
  // A transfer touches THIS month's balances, so deleting one offers to pull it
  // off the current month too (not just future) — otherwise a move you cancelled
  // would still sit on the dashboard reserving the source bank.
  const removeTransfer = async (it) => {
    const choice = await ask({ title: `Remove "${it.title}"?`,
      message: 'This transfer leaves your recurring setup and future months. '
        + "Remove it from THIS month too, or keep this month's move?",
      ok: 'Remove from this month too', alt: 'Future months only',
      cancel: 'Keep it', danger: true });
    if (!choice) return;
    await api(`/budget/recurring/${it.id}/?scope=future`, { method: 'DELETE' });
    if (choice !== 'alt') {
      const line = monthItems.find((m) => String(m.template) === String(it.id)
        || (m.kind === it.kind && (m.title || '').toLowerCase() === (it.title || '').toLowerCase()));
      if (line) { try { await api(`/budget/items/${line.id}/?scope=month`, { method: 'DELETE' }); } catch { /* best effort */ } }
    }
    load();
  };
  const ql = q.trim().toLowerCase();
  const amountOf = (it) => {
    const c = String(it.calculation || '').replace(/[x×]/gi, '*').trim();
    const m = c.match(/^(\d+(?:\.\d+)?)\*(\d+(?:\.\d+)?)$/);
    return m ? Number(m[1]) * Number(m[2]) : (Number(it.amount) || 0);
  };
  /** The instant first pass: a plain substring over the row's visible words. */
  const plainMatch = (i, needle) => !needle
    || [i.title, i.bank_name, i.category_name, i.calculation, i.place_name]
       .some((s) => (s || '').toLowerCase().includes(needle));
  /** One AI filter (from /budget/parse-query/, #300) against a recurring row.
   * range/unpaid describe a month, not a recurring definition, so they pass. */
  const filterMatch = (i, f) => {
    const v = f.value;
    const has = (s) => (s || '').toLowerCase().includes(String(v || '').toLowerCase());
    switch (f.key) {
      case 'amount_gt': return amountOf(i) > Number(v);
      case 'amount_lt': return amountOf(i) < Number(v);
      case 'bank': return has(i.bank_name) || has(i.from_bank_name);
      case 'category': return has(i.category_name);
      case 'place': return has(i.place_name);
      case 'kind': return i.kind === v;
      // A plain recurring line (no cadence) comes once a month, so it is monthly.
      case 'cadence': return v === 'monthly' ? ['monthly', 'none', '', undefined, null].includes(i.cadence) : i.cadence === v;
      default: return true;
    }
  };
  const matches = (i) => (aiQ
    ? aiQ.filters.every((f) => filterMatch(i, f)) && plainMatch(i, (aiQ.text || '').trim().toLowerCase())
    : plainMatch(i, ql));
  // Ask the AI only when the plain pass can't answer: nothing matches the
  // words as typed, or the words read like a filter (amounts, kind, cadence).
  const AI_CUE = /\d|\b(over|under|above|below|more|less|between|income|expenses?|daily|weekly|monthly|every)\b/i;
  const shouldParse = (text) => AI_CUE.test(text)
    || !items.some((i) => plainMatch(i, text.trim().toLowerCase()));
  const RAW_DESC = /\d{4,}|\b(POS|PURCHASE|PURCH|EFT|PMT|DEBIT ORDER|CARD PURCHASE|REF)\b/i;
  const needsName = (it) => it.kind === 'expense' && RAW_DESC.test(it.title || '');
  const isFormula = (it) => it.kind === 'expense'
    && (!!(it.calculation && String(it.calculation).trim()) || (it.cadence && it.cadence !== 'none'));
  const isSpendOnly = (it) => it.kind === 'expense' && !isFormula(it);
  // A transfer is an income line that names a from_bank (the source it moves OUT
  // of). It is neither income nor expense in the totals — only a move.
  const isTransfer = (it) => it.kind === 'income' && !!it.from_bank;
  const chipMatch = (it) => {
    if (filters.size === 0) return true;
    if (filters.has('income') && it.kind === 'income') return true;
    if (filters.has('formula') && isFormula(it)) return true;
    if (filters.has('spendonly') && isSpendOnly(it)) return true;
    if (filters.has('needsname') && needsName(it)) return true;
    return false;
  };
  const expenses = items.filter((i) => i.kind === 'expense' && matches(i) && chipMatch(i));
  const income = items.filter((i) => i.kind === 'income' && matches(i) && chipMatch(i));
  const transfers = income.filter(isTransfer);
  const pureIncome = income.filter((it) => !isTransfer(it));
  const hasPrimarySalary = pureIncome.some((it) => it.salary_primary);
  const isSalaryCheck = (it) => hasPrimarySalary && it.is_salary && !it.salary_primary;
  const incomeTotal = pureIncome.filter((it) => !isSalaryCheck(it)).reduce((a, it) => a + amountOf(it), 0);
  const checkTotal = pureIncome.filter(isSalaryCheck).reduce((a, it) => a + amountOf(it), 0);
  const transferTotal = transfers.reduce((a, it) => a + amountOf(it), 0);
  const expenseTotal = expenses.filter((it) => !testOff.has(it.id)).reduce((a, it) => a + amountOf(it), 0);
  const diff = incomeTotal - expenseTotal;
  const toggleTest = (id) => setTestOff((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const DOW = { '1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun' };
  const ordSuffix = (n) => { const t = n % 100; if (t >= 11 && t <= 13) return 'th'; const u = n % 10; return u === 1 ? 'st' : u === 2 ? 'nd' : u === 3 ? 'rd' : 'th'; };
  // Compact schedule for a row sub-line ("monthly on the 25th").
  const whenShort = (o) => {
    const c = o.cadence;
    if (!c || c === 'none') return 'one-off';
    if (c === 'daily') return `daily${Number(o.times_per_day) > 1 ? ` ×${o.times_per_day}` : ''}`;
    if (c === 'weekly') { const ds = (o.days_of_week || '').split(',').filter(Boolean).map((d) => DOW[d]).filter(Boolean); return ds.length ? `weekly · ${ds.join(', ')}` : 'weekly'; }
    if (c === 'monthly') return o.expected_day ? `monthly on the ${o.expected_day}${ordSuffix(o.expected_day)}` : 'monthly';
    return c;
  };
  // Full phrase for the transfer read-back ("on the 25th of each month").
  const cadenceWords = (o) => {
    const c = o.cadence;
    if (!c || c === 'none') return 'once, and it will not repeat';
    if (c === 'daily') return `every day${Number(o.times_per_day) > 1 ? `, ${o.times_per_day}× a day` : ''}`;
    if (c === 'weekly') { const ds = (o.days_of_week || '').split(',').filter(Boolean).map((d) => DOW[d]).filter(Boolean); return ds.length ? `every ${ds.join(', ')}` : 'weekly'; }
    if (c === 'monthly') return o.expected_day ? `on the ${o.expected_day}${ordSuffix(o.expected_day)} of each month` : 'each month';
    return c;
  };
  const cadenceLine = (it) => {
    if (it.kind === 'income') return it.expected_day ? `monthly · day ${it.expected_day}` : 'monthly';
    const c = it.cadence;
    if (!c || c === 'none') return isFormula(it) ? 'one-off' : 'spend-only';
    if (c === 'daily') return `daily${Number(it.times_per_day) > 1 ? ` ×${it.times_per_day}` : ''}`;
    if (c === 'weekly') {
      const ds = (it.days_of_week || '').split(',').filter(Boolean).map((d) => DOW[d]).filter(Boolean);
      return ds.length ? `weekly · ${ds.join(', ')}` : 'weekly';
    }
    if (c === 'monthly') return `monthly${it.expected_day ? ` · day ${it.expected_day}` : ''}`;
    return c;
  };
  const kindIcon = (it) => (isTransfer(it) ? '⇄' : it.kind === 'income' ? '↓' : '↑');
  const swapTransfer = (it) => askPatch(it, { from_bank: it.bank, bank: it.from_bank }, 'Swap the direction — Out of / Into');
  const openItem = openId != null ? items.find((x) => x.id === openId) : null;
  const cadDirty = !!(openItem && cadDraft && (
    String(cadDraft.cadence || 'none') !== String(openItem.cadence || 'none')
    || String(cadDraft.unit_rate ?? '') !== String(openItem.unit_rate ?? '')
    || String(cadDraft.days_of_week || '') !== String(openItem.days_of_week || '')
    || String(cadDraft.times_per_day || 1) !== String(openItem.times_per_day || 1)
    || String(cadDraft.expected_day ?? '') !== String(openItem.expected_day ?? '')));
  const saveCadence = (it) => {
    const body = { cadence: cadDraft.cadence,
      times_per_day: Number(cadDraft.times_per_day) || 1,
      days_of_week: cadDraft.days_of_week || '' };
    if (cadDraft.unit_rate !== '') body.unit_rate = cadDraft.unit_rate;
    if (cadDraft.expected_day !== '') body.expected_day = Number(cadDraft.expected_day);
    askPatch(it, body, 'Change how often');
  };

  // One accordion row. Three kinds: ↓ income, ⇄ transfer, ↑ expense. Rendered
  // inline (not a nested component) so text inputs keep focus while typing.
  const renderRow = (it) => {
    const open = openId === it.id;
    const warn = needsName(it);
    const spend = isSpendOnly(it);
    const trans = isTransfer(it);
    const inc = it.kind === 'income' && !trans;
    return (
      <div key={it.id} data-guide="recurring-name"
           className={`rounded-xl bg-panel border transition ${open ? 'border-vpurple' : warn ? 'border-amber-400/60 bg-amber-400/[0.04]' : 'border-edge'}`}>
        <button type="button" onClick={() => openRow(it)}
                className="flex items-center gap-3 w-full px-4 py-3 text-left min-h-[56px]"
                title="Click to open — one row at a time">
          <span className={`w-9 h-9 shrink-0 rounded-lg grid place-items-center text-[15px] ${trans ? 'bg-vpurple/20 text-vpurple' : inc ? 'bg-emerald-400/10 text-emerald-400' : 'bg-white/5 text-slate-400'}`}>{kindIcon(it)}</span>
          <span className="flex-1 min-w-0">
            <span className="font-semibold text-sm flex items-center gap-2 flex-wrap">
              <span className="truncate">{it.title}</span>
              {it.active === false && (
                <span className="text-[10px] uppercase tracking-wide border border-edge text-slate-400 rounded px-1.5 py-0.5 shrink-0"
                      title="Paused — it no longer appears in new months">Paused</span>
              )}
              {warn && <span className="text-[9px] uppercase tracking-wide text-amber-400 border border-amber-400/60 rounded px-1.5 py-0.5">Needs a name</span>}
              {!warn && it.kind === 'expense' && <span className="text-[9px] uppercase tracking-wide text-vpurple border border-vpurple/50 rounded px-1.5 py-0.5">{spend ? 'Spend-only' : 'Formula'}</span>}
              {inc && it.is_salary && <span className={`text-[9px] uppercase tracking-wide rounded px-1.5 py-0.5 border ${it.salary_primary ? 'text-amber-400 border-amber-400/60' : 'text-vpurple border-vpurple/50'}`}>{it.salary_primary ? '★ master' : 'salary'}</span>}
            </span>
            <span className="text-[11px] text-slate-500 block truncate mt-0.5">
              {trans
                ? `${it.from_bank_name || 'a bank'} → ${it.bank_name || 'a bank'} · ${whenShort(it)}`
                : [it.bank_name || 'no bank', it.category_name, it.place_name && `📍 ${it.place_name}`, cadenceLine(it)].filter(Boolean).join(' · ')}
            </span>
          </span>
          <span className={`text-sm font-semibold whitespace-nowrap ${trans ? 'text-vpurple' : inc ? 'text-emerald-400' : ''}`}>{fmtR(it.amount)}</span>
          <span className={`text-slate-500 text-xs shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
        </button>
        {open && (
          <div className="px-4 pb-4 flex flex-col gap-3">
            <div className="text-[10px] uppercase tracking-[0.11em] text-vpurple">{warn ? 'Raw import descriptor — name it' : trans ? 'How this move is set up' : 'How it is set up'}</div>

            <div>
              <label className="block text-[11px] text-slate-500 mb-1">Name</label>
              <input className="input text-sm w-full" defaultValue={it.title}
                     key={`t${it.id}:${it.title}`}
                     title="Name it what YOU call it — saved on blur/Enter"
                     onBlur={(e) => saveRename(it, e.target.value)}
                     onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} />
              {warn && <p className="text-[11px] text-slate-500 mt-1">Renaming re-derives the category and finds the place on your Spend Map.</p>}
            </div>

            {it.kind === 'expense' && spend && (
              <p className="text-[11px]" style={{ color: 'var(--color-accent-400, #a99ee4)' }}>
                Spend-only — no units to tick. Whatever you log is what it cost; Vault totals it against the budget.
              </p>
            )}

            {trans && (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 160px' }}>
                  <label className="block text-[11px] text-slate-500 mb-1">Out of</label>
                  <select className="input text-sm w-full" value={it.from_bank || ''}
                          onChange={(e) => patch(it, { from_bank: e.target.value || null }, 'Change the Out-of bank')}>
                    <option value="">choose a bank…</option>
                    {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div style={{ flex: 'none', fontSize: '17px', paddingBottom: '10px', color: 'var(--color-accent, #9184d9)' }}>→</div>
                <div style={{ flex: '1 1 160px' }}>
                  <label className="block text-[11px] text-slate-500 mb-1">Into</label>
                  <select className="input text-sm w-full" value={it.bank || ''}
                          onChange={(e) => patch(it, { bank: e.target.value || null }, 'Change the Into bank')}>
                    <option value="">choose a bank…</option>
                    {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <label className="block text-[11px] text-slate-500 mb-1">How much</label>
                  <input className="input text-sm w-full text-right" key={`a${it.id}:${it.amount}`}
                         defaultValue={it.amount}
                         onBlur={(e) => { const v = (e.target.value || '0').replace(/[x×]/gi, '*').trim(); if (v !== String(it.amount)) patch(it, { amount: v || '0', calculation: '' }, `Set the move to ${v || '0'}`); }} />
                </div>
              </div>
            )}

            {/* Spend-only expenses log what you spend — no units, so no
                cadence/unit control here (parity with the add form). */}
            {cadDraft && open && !(it.kind === 'expense' && spend) && (
              <div className="border border-edge rounded-xl">
                {/* Collapsible "Repeat · advanced" — the full cadence (Daily/
                    Weekly which days/Monthly which date) tucked behind a chevron,
                    with the current schedule shown on the closed header. */}
                <button type="button"
                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left"
                        onClick={() => setCadAdvOpen((v) => !v)}>
                  <span className="text-[10px] uppercase tracking-[0.11em] text-vpurple">⚙ Repeat · advanced</span>
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-[11px] text-slate-500 truncate">{cadenceLine(it)}</span>
                    <span className={`text-slate-500 text-xs shrink-0 transition-transform ${cadAdvOpen ? 'rotate-180' : ''}`}>▾</span>
                  </span>
                </button>
                {cadAdvOpen && (
                  <div className="px-3 pb-3 pt-1 border-t border-edge">
                    <CadenceControls value={cadDraft} onChange={setCadDraft}
                                     hideRate={openItem && openItem.kind === 'income'} />
                    {cadDirty && (
                      <button className="btn-primary !py-2 text-sm mt-3"
                              onClick={() => saveCadence(it)}>Save schedule</button>
                    )}
                  </div>
                )}
              </div>
            )}

            {trans && (
              <div className="rounded-xl px-3.5 py-3 bg-vpurple/10 border border-vpurple/30">
                <div className="text-[13px] leading-relaxed text-vpurple">
                  Moves {fmtR(it.amount)} out of {it.from_bank_name || 'a bank'} and into {it.bank_name || 'a bank'} {cadenceWords(cadDraft && open ? { ...it, ...cadDraft } : it)}.
                </div>
                <div className="text-[11px] leading-relaxed mt-1 text-slate-400">
                  Neither your income nor your expenses change — this only moves money you already have. Untick it and both balances go straight back.
                </div>
              </div>
            )}

            {!trans && (
              <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                <div>
                  <label className="block text-[11px] text-slate-500 mb-1">{inc ? 'Amount or formula' : spend ? 'Monthly budget' : 'Amount or formula — unit × count'}</label>
                  <input className="input text-sm w-full text-right" data-guide="recurring-amount"
                         key={`a${it.id}:${it.calculation || it.amount}`}
                         defaultValue={it.calculation || it.amount}
                         title="Amount or formula (e.g. 200*31)"
                         onBlur={(e) => {
                           const v = e.target.value.replace(/[x×]/gi, '*').trim();
                           if (v === (it.calculation || String(it.amount))) return;
                           if (/[+*/-]/.test(v.slice(1))) patch(it, { calculation: v }, `Set to ${v}`);
                           else patch(it, { amount: v || '0', calculation: '' }, `Set to ${v || '0'}`);
                         }} />
                  {it.calculation && <p className="text-[11px] text-slate-500 mt-1">= {fmtR(it.amount)} today</p>}
                </div>
                {it.kind === 'expense' && (
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1">Category</label>
                    <CategoryPicker categories={categories} value={it.category}
                                    title={it.title} kind={it.kind}
                                    key={`cat${it.id}:${it.category ?? ''}`}
                                    onPicked={(id) => patch(it, { category: id }, 'Change the category')} />
                  </div>
                )}
                <div>
                  <label className="block text-[11px] text-slate-500 mb-1">{inc ? 'Lands in' : 'Bank'}</label>
                  <select className="input text-sm w-full" value={it.bank || ''}
                          onChange={(e) => patch(it, { bank: e.target.value || null }, 'Change the bank')}>
                    <option value="">no bank</option>
                    {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                {inc && (
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1">Make it a transfer (out of…)</label>
                    <select className="input text-sm w-full" value={it.from_bank || ''}
                            title="If this line only moves money out of one of your banks into the bank above, name the source — it becomes a Transfer, not new income"
                            onChange={(e) => patch(it, { from_bank: e.target.value || null }, e.target.value ? 'Make it a transfer' : 'Back to plain income')}>
                      <option value="">— not a transfer —</option>
                      {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                )}
                {it.kind === 'expense' && (
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1">Location</label>
                    <select className="input text-sm w-full" value={it.place || ''}
                            title="Link this recurring item to a place on your Spend Map (#299)"
                            onChange={(e) => linkPlace(it, e.target.value || null)}>
                      <option value="">📍 no location</option>
                      {places.map((pp) => <option key={pp.id} value={pp.id}>{pp.name}</option>)}
                    </select>
                  </div>
                )}
                {inc && (
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1">Lands on day</label>
                    <input className="input text-sm w-full" type="number" min="1" max="28"
                           key={`d${it.id}:${it.expected_day ?? ''}`}
                           placeholder="1–28" defaultValue={it.expected_day ?? ''}
                           title="Day of month it normally lands (1–28)"
                           onBlur={(e) => {
                             const v = e.target.value ? Number(e.target.value) : null;
                             if (v !== it.expected_day) patch(it, { expected_day: v });
                           }} />
                  </div>
                )}
              </div>
            )}

            {inc && (
              <div className="flex gap-2 flex-wrap">
                <button className={`text-sm px-3 py-2 rounded-xl border min-h-[44px] ${it.is_salary ? 'border-vpurple text-vpurple bg-vpurple/10' : 'border-edge text-slate-400'}`}
                        title="A bank's salary paid-in check — a portion of the master landing there"
                        onClick={() => patch(it, { is_salary: !it.is_salary })}>
                  {it.is_salary ? 'salary ✓' : 'salary?'}
                </button>
                {it.is_salary && (
                  <button className={`text-sm px-3 py-2 rounded-xl border min-h-[44px] ${it.salary_primary ? 'border-amber-400 text-amber-400 bg-amber-400/10' : 'border-edge text-slate-400'}`}
                          title="The ONE master salary — counts once in the month total; crowning dethrones any other"
                          onClick={() => patch(it, { salary_primary: !it.salary_primary })}>
                    {it.salary_primary ? '★ master' : '☆ master?'}
                  </button>
                )}
              </div>
            )}

            {it.kind === 'expense' && (
              <label className="flex items-center gap-2 text-[11px] text-slate-500 cursor-pointer">
                <input type="checkbox" checked={!testOff.has(it.id)} onChange={() => toggleTest(it.id)} />
                Include in the income-vs-expense difference
              </label>
            )}

            <div className="flex gap-2 flex-wrap">
              {trans ? (
                <button className="text-left border border-vpurple/60 text-vpurple rounded-xl px-3 py-2.5 text-sm hover:bg-vpurple/10 transition min-h-[44px]"
                        onClick={() => swapTransfer(it)}>⇄ Swap the direction</button>
              ) : (
                <button className="text-left border border-vpurple/60 text-vpurple rounded-xl px-3 py-2.5 text-sm hover:bg-vpurple/10 transition min-h-[44px]"
                        data-guide="recurring-ai" disabled={aiBusy !== null}
                        onClick={() => redoAI(it)}>
                  {aiBusy === it.id ? '✨ …' : '✨ Redo from the name'}
                </button>
              )}
              {!trans && (
                <button className="text-left border border-edge rounded-xl px-3 py-2.5 text-sm hover:border-vblue transition min-h-[44px]"
                        onClick={() => setMerging(it)}>⇄ Merge another in</button>
              )}
              <button className="text-left border border-edge rounded-xl px-3 py-2.5 text-sm hover:border-vblue transition min-h-[44px]"
                      onClick={async () => {
                        const d = await api('/budget/recurring/seed_forward/', { method: 'POST' });
                        setAiMsg(`✓ ${d.synced} recurring item${d.synced === 1 ? '' : 's'} synced into current & prepared months.`);
                        load();
                      }}>↻ Sync into months</button>
              {!onThisMonth(it) && (
                <button className="text-left border border-edge rounded-xl px-3 py-2.5 text-sm text-vblue hover:border-vblue transition min-h-[44px]"
                        onClick={async () => {
                          try {
                            const r = await api(`/budget/recurring/${it.id}/add_to_month/`, { method: 'POST', body: { month: curMonth } });
                            await ask({ title: r?.created === false ? 'Already on this month' : 'Added to this month',
                              message: r?.created === false
                                ? `"${it.title}" is already on ${r?.month || curMonth}.`
                                : `"${it.title}" is on ${r?.month || curMonth} now.`, ok: 'OK' });
                            load();
                          } catch (err) {
                            await ask({ title: 'Could not add', message: err.data?.detail || err.message || 'Try again.', ok: 'OK' });
                          }
                        }}>＋ Add to this month</button>
              )}
              {!trans && (
                <button className="text-left border border-edge rounded-xl px-3 py-2.5 text-sm text-slate-300 hover:border-vblue transition min-h-[44px]"
                        title={it.active === false
                          ? 'Start adding it to new months again'
                          : 'Stop adding it to new months — nothing is deleted'}
                        onClick={async () => {
                          await api(`/budget/recurring/${it.id}/`, { method: 'PATCH', body: { active: it.active === false } });
                          load();
                        }}>{it.active === false ? '▶ Resume this recurring' : '⏸ Pause this recurring'}</button>
              )}
              <button className="text-left border border-red-400/50 text-red-400 rounded-xl px-3 py-2.5 text-sm hover:bg-red-400/10 transition min-h-[44px]"
                      onClick={() => (trans ? removeTransfer(it) : remove(it))}>{trans ? 'Remove the transfer' : 'Delete'}</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <p className="text-sm text-slate-400 mb-4">
        Every definition of your money, copied into each new month. Three kinds:
        what comes in, what moves between your banks, and what goes out.
      </p>

      {/* Summary strip — grid auto-fit, reflows with no media query. */}
      <div className="grid gap-2.5 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <div className="card !p-3.5">
          <div className="text-lg font-semibold text-emerald-400">{fmtR(incomeTotal)}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">Recurring income</div>
          <div className="text-[11px] text-slate-600">{pureIncome.length} item{pureIncome.length === 1 ? '' : 's'}</div>
        </div>
        <div className="card !p-3.5">
          <div className="text-lg font-semibold text-vpurple">R 0,00 net</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">Transfers</div>
          <div className="text-[11px] text-slate-600">{transfers.length} move{transfers.length === 1 ? '' : 's'} · {fmtR(transferTotal)} shifted</div>
        </div>
        <div className="card !p-3.5">
          <div className="text-lg font-semibold text-red-400">{fmtR(expenseTotal)}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">Recurring expenses</div>
          <div className="text-[11px] text-slate-600">{expenses.length} item{expenses.length === 1 ? '' : 's'}</div>
        </div>
        <div className="card !p-3.5">
          <div className={`text-lg font-semibold ${diff < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{fmtR(diff)}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">Difference</div>
          <div className="text-[11px] text-slate-600">transfers excluded</div>
        </div>
      </div>

      {(checkTotal > 0 || testOff.size > 0) && (
        <div className="mb-3 text-[11px] space-y-1">
          {checkTotal > 0 && <p className="text-slate-500">Salary checks on other banks ({fmtR(checkTotal)}) are the master salary moving between banks — not counted again.</p>}
          {testOff.size > 0 && <p className="text-vpurple">Testing with {testOff.size} expense{testOff.size === 1 ? '' : 's'} disabled · <button className="underline" onClick={() => setTestOff(new Set())}>reset</button></p>}
        </div>
      )}

      <div className="mb-2">
        {/* Plain substring first (instant); the AI parse (amounts, bank,
            category, income/expense, daily/weekly/monthly) takes over only when
            the words aren't a plain match (#300). Its chips are removable. */}
        <SearchField value={q} onChange={(v) => { setQ(v); if (!v.trim()) setAiQ(null); }}
                     placeholder="🔎 Search title, bank, amount, cadence…"
                     shouldParse={shouldParse}
                     onFilters={setAiQ}
                     aiLine={ql ? `${aiQ ? `Showing ${aiQ.filters.map((f) => f.label).join(', ')}${aiQ.text ? ` · “${aiQ.text}”` : ''} — ` : ''}${income.length + expenses.length} match${income.length + expenses.length === 1 ? '' : 'es'}` : ''}
                     chips={aiQ ? [] : (q ? [{ key: 'q', label: `“${q}”`, onRemove: () => setQ('') }] : [])} />
      </div>
      <div className="flex gap-1.5 flex-wrap mb-3">
        {[['income', 'Income'], ['formula', 'Formula'], ['spendonly', 'Spend-only'], ['needsname', 'Needs a name']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => toggleFilter(k)}
                  className={`rounded-full border px-3 min-h-[38px] text-xs transition ${filters.has(k) ? 'border-vpurple text-vpurple bg-vpurple/10' : 'border-edge text-slate-400 hover:border-slate-500'}`}>{l}</button>
        ))}
      </div>

      {aiMsg && <p className="text-xs text-vpurple mb-3">{aiMsg}</p>}

      {ql && !income.length && !expenses.length && (
        <p className="text-slate-600 text-sm mb-3">Nothing matches “{q}”.</p>
      )}

      {pureIncome.length > 0 && <>
        <h3 className="text-[11px] uppercase tracking-wide text-emerald-400/80 mb-2">Income · {pureIncome.length}</h3>
        <div className="grid gap-2 mb-4">{pureIncome.map(renderRow)}</div>
      </>}

      {transfers.length > 0 && <>
        <div className="flex items-baseline gap-2 mb-1">
          <h3 className="text-[11px] uppercase tracking-wide text-vpurple">Transfers · {transfers.length} item{transfers.length === 1 ? '' : 's'}</h3>
          <span className="text-[11px] text-slate-600">·</span>
          <span className="text-[11px] font-medium text-vpurple">nets to R 0,00</span>
        </div>
        <p className="text-[11px] text-slate-500 mb-2 max-w-xl leading-relaxed">Money moving between your own banks. Counted in neither total — a transfer changes where your money sits, never how much you have.</p>
        <div className="grid gap-2 mb-4">{transfers.map(renderRow)}</div>
      </>}

      <h3 className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Expenses · {expenses.length}</h3>
      <div className="grid gap-2">
        {expenses.map(renderRow)}
        {!expenses.length && <p className="text-slate-600 text-sm py-3">
          Nothing recurring yet — imports and the + sheet can add items here.
        </p>}
      </div>

      <div className="mt-5 border-t border-edge pt-4 flex gap-2 flex-wrap">
        <select className="input !w-auto" value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}>
          <option value="expense">Expense</option>
          <option value="income">Income</option>
          <option value="transfer">Transfer</option>
        </select>
        <input className="input flex-1 min-w-[120px]" placeholder="Title…" value={form.title}
               onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <input className="input !w-32" placeholder={form.kind === 'transfer' ? 'How much' : 'Amount or 200*31'} value={form.amount}
               onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        <select className="input !w-auto" value={form.bank}
                onChange={(e) => setForm({ ...form, bank: e.target.value })}>
          <option value="">{form.kind === 'expense' ? 'Bank…' : form.kind === 'transfer' ? 'Into…' : 'Lands in…'}</option>
          {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <button className="btn-primary" onClick={add}>Add</button>
      </div>
      {form.kind === 'transfer' && (
        <div className="mt-3 flex gap-2 flex-wrap items-center">
          <span className="text-[11px] text-slate-500">Out of:</span>
          <select className="input !w-auto text-sm" value={form.from_bank}
                  onChange={(e) => setForm({ ...form, from_bank: e.target.value })}>
            <option value="">choose the source bank…</option>
            {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <span className="text-vpurple">→ into the bank chosen above</span>
          {form.from_bank && form.bank && (
            <span className="text-[11px] w-full" style={{ color: 'var(--color-accent-400, #a99ee4)' }}>
              Moves money out of {banks.find((b) => String(b.id) === String(form.from_bank))?.name || 'a bank'} and into {banks.find((b) => String(b.id) === String(form.bank))?.name || 'a bank'} — not new income, just a move between your banks.
            </span>
          )}
        </div>
      )}
      {form.kind === 'income' && (
        <div className="mt-3 flex gap-2 flex-wrap items-center">
          <span className="text-[11px] text-slate-500">Make it a transfer — out of (optional):</span>
          <select className="input !w-auto text-sm" value={form.from_bank}
                  onChange={(e) => setForm({ ...form, from_bank: e.target.value })}>
            <option value="">— not a transfer —</option>
            {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {form.from_bank && (
            <span className="text-[11px]" style={{ color: 'var(--color-accent-400, #a99ee4)' }}>
              This becomes a Transfer — money moving out of {banks.find((b) => String(b.id) === String(form.from_bank))?.name || 'a bank'} into the bank above.
            </span>
          )}
        </div>
      )}
      {form.kind === 'expense' && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {[['formula', 'Formula — units to tick'], ['spend', 'Spend-only — log what you spend']].map(([k, l]) => (
            <button key={k} type="button"
                    className={`rounded-lg border px-3 py-1.5 text-sm transition ${((form.spend_only ? 'spend' : 'formula') === k) ? 'border-vblue text-vblue bg-vblue/10' : 'border-edge text-slate-400 hover:border-slate-500'}`}
                    onClick={() => setForm({ ...form, spend_only: k === 'spend' })}>{l}</button>
          ))}
        </div>
      )}
      {(form.kind === 'income' || form.kind === 'transfer' || (form.kind === 'expense' && !form.spend_only)) && (
        <div className="mt-3 border border-edge rounded-xl px-3 py-3">
          <CadenceControls value={form} hideRate={form.kind !== 'expense'}
                           onChange={(cad) => setForm({ ...form, ...cad })} />
        </div>
      )}
      {form.kind === 'expense' && form.spend_only && (
        <p className="mt-3 text-[11px]" style={{ color: 'var(--color-accent-400, #a99ee4)' }}>
          Spend-only — no units to tick. Whatever you log is what it cost; Vault totals it against the budget above.
        </p>
      )}
      <div className="mt-3 flex gap-4">
        <button className="text-xs text-vblue hover:text-white transition"
                title="Push every recurring item into the current and prepared months where it's missing — each month's copy stays deletable"
                onClick={async () => {
                  const d = await api('/budget/recurring/seed_forward/', { method: 'POST' });
                  setAiMsg(`✓ ${d.synced} recurring item${d.synced === 1 ? '' : 's'} synced into current & prepared months.`);
                  load();
                }}>
          ↻ Sync into current & future months
        </button>
        <button className="text-xs text-slate-600 hover:text-red-400 transition"
                title="Delete EVERY recurring item (confirm first) — existing months keep their lines"
                onClick={() => setClearingAll(true)}>
          Delete all recurring…
        </button>
      </div>

      {merging && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6"
             onClick={() => setMerging(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" />
          <div className="relative bg-panel border border-edge rounded-2xl p-6 max-w-sm w-full shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold">Merge into “{merging.title}”</h3>
            <p className="text-sm text-slate-400 mt-1 mb-3">
              Pick the recurring item that folds in. Formulas combine
              (a + b), the amount recalculates, its month lines follow, and
              the picked item is deleted.
            </p>
            <div className="grid gap-1.5 max-h-56 overflow-y-auto">
              {items.filter((x) => x.kind === merging.kind && x.id !== merging.id)
                .map((x) => (
                <button key={x.id}
                        className="text-left border border-edge rounded-xl px-3 py-2 text-sm
                                   hover:border-vblue transition"
                        onClick={async () => {
                          await api(`/budget/recurring/${merging.id}/merge/`,
                                    { method: 'POST', body: { other: x.id } });
                          setAiMsg(`⇄ “${x.title}” merged into “${merging.title}” — new formula = `
                                   + `${merging.calculation || merging.amount}+${x.calculation || x.amount}`);
                          setMerging(null); load();
                        }}>
                  {x.title}
                  <span className="block text-xs text-slate-500">
                    {x.calculation || fmtR(x.amount)}
                    {' → combined: '}{merging.calculation || merging.amount}+{x.calculation || x.amount}
                  </span>
                </button>
              ))}
              {items.filter((x) => x.kind === merging.kind && x.id !== merging.id).length === 0 && (
                <p className="text-xs text-slate-600">No other {merging.kind} items to merge.</p>
              )}
            </div>
            <button className="mt-3 text-xs text-slate-500" onClick={() => setMerging(null)}>Cancel</button>
          </div>
        </div>
      )}

      {clearingAll && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6"
             onClick={() => setClearingAll(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" />
          <div className="relative bg-panel border border-edge rounded-2xl p-6 max-w-sm w-full shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold">Delete ALL recurring items?</h3>
            <p className="text-sm text-slate-400 mt-2">
              Your entire recurring setup ({items.length} item{items.length === 1 ? '' : 's'})
              is removed — new months start empty until you rebuild it. Months that
              already exist keep their lines. This can't be undone.
            </p>
            <div className="mt-4 flex gap-3">
              <button className="flex-1 border border-red-400/60 text-red-400 rounded-xl py-2.5
                                 hover:bg-red-400/10 transition"
                      onClick={async () => {
                        await api('/budget/recurring/delete_all/', { method: 'POST' });
                        setClearingAll(false); load();
                      }}>
                Delete all
              </button>
              <button className="flex-1 border border-edge rounded-xl py-2.5"
                      onClick={() => setClearingAll(false)}>
                Keep them
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingEdit && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6"
             onClick={() => { setPendingEdit(null); load(); }}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" />
          <div className="relative bg-panel border border-edge rounded-2xl p-6 max-w-sm w-full shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold mb-1">{pendingEdit.it.title}</h3>
            <p className="text-sm text-slate-300 mb-1">{pendingEdit.label}</p>
            <p className="text-sm text-slate-400 mb-4">
              Recurring changes normally start from next month. Update{' '}
              <span className="text-white font-medium">{monthLabel(curMonth)}</span>{' '}
              too, so your dashboard and map change right away?
            </p>
            <div className="grid gap-2">
              <button className="btn-primary w-full" onClick={() => commitEdit(true)}>
                Yes — update {monthLabel(curMonth)} too
              </button>
              <button className="w-full py-2.5 rounded-xl border border-edge text-sm
                                 text-slate-300 hover:border-slate-500 transition"
                      onClick={() => commitEdit(false)}>
                No — from next month only
              </button>
              <button className="text-xs text-slate-500 hover:text-slate-300 py-1"
                      onClick={() => { setPendingEdit(null); load(); }}>
                Cancel this change
              </button>
            </div>
            <p className="text-[11px] text-slate-600 mt-3">
              Updating {monthLabel(curMonth)} keeps what you’ve already spent and
              ticked off — only the name, amount, category, bank and map place change.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/** Learned import rules — every decision made on a transaction, made
 * PERMANENT and changeable: which merchant routes to which expense on future
 * imports. Pause one to stop it applying (it stays here), or delete it so
 * the merchant is interpreted fresh next time. */
function Rules() {
  const [rules, setRules] = useState([]);
  const load = useCallback(() =>
    api('/budget/reconcile-rules/').then((d) => setRules(d.results ?? d)), []);
  useEffect(() => { load(); }, [load]);
  const patch = async (r, body) => {
    await api(`/budget/reconcile-rules/${r.id}/`, { method: 'PATCH', body }); load();
  };
  const remove = async (r) => {
    await api(`/budget/reconcile-rules/${r.id}/`, { method: 'DELETE' }); load();
  };
  const [q, setQ] = useState('');
  // A pattern carrying a masked account number matches too much.
  const tooBroad = (r) => /(\*{2,}|\u2022{2,}|x{3,})\s*\d|\d{6,}/i.test(r.match_text || '');
  const narrow = async (r) => {
    const v = await ask({ title: 'Narrow the pattern',
      message: 'This pattern includes a masked account number, so it can match unrelated lines. Edit it to something specific to the merchant.',
      input: true, value: r.match_text, ok: 'Save pattern' });
    if (v && v !== r.match_text) patch(r, { match_text: v });
  };
  return (
    <div>
      {/* Four-card summary strip (Pass 7) — Active / Paused / Matched / Needs review. */}
      {(() => {
        const activeN = rules.filter((r) => r.active).length;
        const pausedN = rules.filter((r) => !r.active).length;
        const matched = rules.reduce((a, r) => a + (Number(r.hits) || 0), 0);
        const review = rules.filter((r) => tooBroad(r)).length;
        const cards = [
          ['Active rules', String(activeN), 'applying on import'],
          ['Paused', String(pausedN), 'kept, not applying'],
          ['Matched this year', String(matched), 'lines filed'],
          ['Needs review', String(review), 'may be too broad'],
        ];
        return (
          <div className="grid gap-2 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))' }}>
            {cards.map(([k, v, sub]) => (
              <div key={k} className={`rounded-xl border px-4 py-3 ${k === 'Needs review' && review > 0 ? 'border-amber-400/50 bg-amber-400/[0.04]' : 'border-edge bg-white/[0.03]'}`}>
                <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
                <div className="text-lg font-bold text-slate-100">{v}</div>
                <div className="text-[10px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>
        );
      })()}
      <p className="text-sm text-slate-400 mb-4">
        Decisions you made on imports live here: “this merchant always belongs
        to that expense”. Pause a rule to stop it applying; delete it to start
        fresh. Imports always show you what a rule did — nothing is silent.
      </p>
      <div className="mb-3">
        <SearchField value={q} onChange={setQ} placeholder="Search your rules…"
          aiLine={q.trim() ? `${rules.filter((r) => (r.match_text || '').toLowerCase().includes(q.trim().toLowerCase())).length} match${rules.filter((r) => (r.match_text || '').toLowerCase().includes(q.trim().toLowerCase())).length === 1 ? '' : 'es'}` : ''} />
      </div>
      {(() => {
        const shown = rules.filter((r) => !q.trim() || (r.match_text || '').toLowerCase().includes(q.trim().toLowerCase()));
        const active = shown.filter((r) => r.active);
        const paused = shown.filter((r) => !r.active);
        const row = (r) => (
          <div key={r.id}
               className={`border rounded-xl px-4 py-3 flex items-center gap-3 ${
                 tooBroad(r) ? 'border-amber-400/50' : r.active ? 'border-edge' : 'border-edge/50 opacity-60'}`}>
            <span className="flex-1 min-w-0">
              <span className="flex items-center gap-2">
                <span className="text-sm truncate">“{r.match_text}”</span>
                {tooBroad(r) && (
                  <span className="text-[9px] uppercase tracking-wide text-amber-400 border border-amber-400/50 rounded px-1.5 py-0.5 shrink-0">too broad</span>
                )}
                {Number(r.hits) > 0 && (
                  <span className="text-[10px] text-slate-500 shrink-0">{r.hits}× used</span>
                )}
              </span>
              <span className="block text-xs text-slate-500">
                → {(r.allocations || []).map((a) => a.title).filter(Boolean).join(', ') || 'its expense'}
              </span>
            </span>
            {tooBroad(r) && (
              <button className="text-xs border border-amber-400/50 text-amber-400 rounded-lg px-2 py-1 hover:bg-amber-400/10 transition"
                      onClick={() => narrow(r)}>Narrow the pattern</button>
            )}
            <button className="text-xs border border-edge rounded-lg px-2 py-1 text-slate-400 hover:border-vblue"
                    onClick={() => patch(r, { active: !r.active })}>
              {r.active ? 'Pause' : 'Resume'}
            </button>
            <button className="text-slate-600 hover:text-red-400" title="Delete this rule"
                    onClick={() => remove(r)}>✕</button>
          </div>
        );
        if (!shown.length) {
          return <p className="text-slate-600 text-sm py-3">No rules yet — group or route transactions during an import and the decisions land here.</p>;
        }
        return (
          <div className="grid gap-4">
            {active.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Active · {active.length}</h3>
                <div className="grid gap-2">{active.map(row)}</div>
              </div>
            )}
            {paused.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Paused · {paused.length}</h3>
                <div className="grid gap-2">{paused.map(row)}</div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function Savings() {
  const [data, setData] = useState(null);
  const [tx, setTx] = useState({ kind: 'deposit', amount: '', note: '' });
  const load = useCallback(() => api('/budget/savings/').then(setData), []);
  useEffect(() => { load(); }, [load]);
  const addTx = async () => {
    if (!tx.amount) return;
    await api('/budget/savings-transactions/', { method: 'POST', body: tx });
    setTx({ kind: 'deposit', amount: '', note: '' }); load();
  };
  // Two explicit buttons (mockup) — the direction is the button, not a dropdown.
  const record = async (kind) => {
    if (!(Number(tx.amount) > 0)) return;
    await api('/budget/savings-transactions/', { method: 'POST', body: { ...tx, kind } });
    setTx({ kind: 'deposit', amount: '', note: '' }); load();
  };
  const setBaseline = async () => {
    // The previous month's closing is the natural baseline — offered as the
    // default; any figure works.
    const ledger = data?.ledger || [];
    const prevClosing = ledger.length > 1 ? ledger[ledger.length - 2].closing
      : (ledger.length === 1 ? ledger[0].closing : data?.baseline);
    const v = await ask({
      title: 'Past savings baseline',
      message: 'Savings already banked before Vault started tracking. '
        + `Pre-filled with last month's closing (${fmtR(prevClosing)}).`,
      input: true, value: prevClosing, ok: 'Set baseline',
    });
    if (v !== null) {
      await api('/budget/savings/', { method: 'PATCH', body: { opening_balance: v } });
      load();
    }
  };
  const setPot = async () => {
    // Set what the pot ACTUALLY holds — the difference records as a pure
    // adjustment (deposit/withdrawal with NO bank movement).
    const v = await ask({
      title: 'Set the savings pot',
      message: `It reads ${fmtR(data.balance)} now. Enter what it actually `
        + 'holds — the difference is recorded as an adjustment; no bank '
        + 'balance moves.',
      input: true, value: data.balance, ok: 'Set pot',
    });
    if (v === null || v === '') return;
    const diff = Number(v) - Number(data.balance);
    if (!diff) return;
    await api('/budget/savings-transactions/',
              { method: 'POST',
                body: { kind: diff > 0 ? 'deposit' : 'withdrawal',
                        amount: Math.abs(diff).toFixed(2),
                        note: 'Pot set by hand', bank: '' } });
    load();
  };
  const setTarget = async () => {
    // A blank answer clears the goal; any figure sets it. The pot's own balance
    // stays the single source of "how far" — the target only says "how far to".
    const v = await ask({
      title: 'Savings target',
      message: 'A goal to aim the pot at — the row shows your progress toward it. '
        + 'Leave it blank to clear the target.',
      input: true, value: data.target ?? '', ok: 'Set target',
    });
    if (v === null) return;
    await api('/budget/savings/', { method: 'PATCH', body: { target: v } });
    load();
  };
  if (!data) return <p className="text-slate-500">Loading…</p>;
  // Progress toward the goal, from the SAME pot balance the row shows — no
  // second calc. Null target = no goal, so the bar and % stay hidden.
  const targetNum = data.target != null && data.target !== '' ? Number(data.target) : null;
  const pct = targetNum && targetNum > 0
    ? Math.max(0, Math.min(100, Math.round((Number(data.balance || 0) / targetNum) * 100)))
    : null;
  return (
    <div>
      {/* Four-card summary strip (Pass 7) — real data: In the pot / Baseline /
          Target / Net this year. Target is optional (SavingsAccount.target). */}
      {(() => {
        const yr = String(new Date().getFullYear());
        const netYr = (data.ledger || []).filter((r) => String(r.month).startsWith(yr)).reduce((a, r) => a + Number(r.amount || 0), 0);
        const cards = [
          ['In the pot', fmtR(data.balance), data.savings_bank || 'set aside', ''],
          ['Baseline', fmtR(data.baseline), 'where it started', ''],
          ['Target', targetNum ? fmtR(targetNum) : '—', pct != null ? `${pct}% there` : 'set a goal', ''],
          ['Net this year', fmtR(netYr), 'deposits − withdrawals', netYr < 0 ? 'text-red-400' : 'text-emerald-400'],
        ];
        return (
          <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))' }}>
            {cards.map(([k, v, sub, cls]) => (
              <div key={k} className="rounded-xl border border-edge bg-white/[0.03] px-4 py-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
                <div className={`text-lg font-bold ${cls || 'text-slate-100'}`}>{v}</div>
                <div className="text-[10px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>
        );
      })()}
      <div className="rounded-2xl border border-edge p-6 text-center bg-ink/50">
        <div className="text-slate-400 text-xs uppercase tracking-wide">Savings pot</div>
        <button className="text-4xl font-extrabold mt-1 bg-vault-gradient bg-clip-text
                           text-transparent hover:opacity-80 transition"
                title="Tap to SET what the pot actually holds — the difference records as an adjustment"
                onClick={setPot}>
          {fmtR(data.balance)}
        </button>
        <div className="text-xs text-slate-500 mt-1">
          in {data.savings_bank} ·{' '}
          <button className="underline hover:text-slate-300" onClick={setBaseline}>
            baseline {fmtR(data.baseline)}
          </button>
          {' · '}
          <button className="underline hover:text-slate-300" onClick={setTarget}>
            {targetNum ? `target ${fmtR(targetNum)}` : 'set a target'}
          </button>
        </div>
        {/* Target progress — the pot balance measured against the goal. Wide
            only in spirit; here it renders whenever a target exists. */}
        {pct != null && (
          <div className="mt-3 max-w-md mx-auto">
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-vault-gradient" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {pct}% of {fmtR(targetNum)}
            </div>
          </div>
        )}
      </div>
      <div className="mt-4 flex gap-2 flex-wrap items-center">
        <input className="input !w-32" placeholder="Amount" inputMode="decimal" value={tx.amount}
               onChange={(e) => setTx({ ...tx, amount: e.target.value })} />
        <input className="input flex-1 min-w-[120px]" placeholder="Note (optional)" value={tx.note}
               onChange={(e) => setTx({ ...tx, note: e.target.value })} />
        <button className="border border-emerald-400/50 text-emerald-400 rounded-xl px-4 py-2 text-sm hover:bg-emerald-400/10 transition disabled:opacity-40"
                disabled={!(Number(tx.amount) > 0)} onClick={() => record('deposit')}>↘ Record a deposit</button>
        <button className="border border-vpurple/50 text-vpurple rounded-xl px-4 py-2 text-sm hover:bg-vpurple/10 transition disabled:opacity-40"
                disabled={!(Number(tx.amount) > 0)} onClick={() => record('withdrawal')}>↗ Record a withdrawal</button>
      </div>
      <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
        <span className="text-emerald-400">↘ Deposit</span> moves money out of {data.savings_bank} into the pot — leftover drops.{' '}
        <span className="text-vpurple">↗ Withdraw</span> comes back as income on {data.savings_bank} — leftover rises. Both reversible.
      </p>
      <h3 className="text-xs uppercase tracking-wide text-slate-500 mt-6 mb-2">Month by month</h3>
      <div className="grid gap-1.5 text-sm">
        {(data.ledger || []).slice().reverse().map((r) => (
          <div key={r.month} className="flex items-center gap-3 border border-edge rounded-xl px-4 py-2.5">
            <span className="w-20 text-slate-400">{r.month}</span>
            <span className="flex-1 text-emerald-400">+{fmtR(r.deposits)}</span>
            <span className="flex-1 text-red-400">−{fmtR(r.withdrawals)}</span>
            <span className="font-semibold">{fmtR(r.closing)}</span>
          </div>
        ))}
        {!(data.ledger || []).length && (
          <p className="text-slate-600 py-3">No savings movements yet.</p>
        )}
      </div>
    </div>
  );
}


// #money-spots — cash you physically hold or stash (wallet, safe, envelope),
// apart from bank accounts. Money in from a bank (withdrawal, moves the bank too)
// or from another person (external); spend from it with a place + which expense;
// every movement is a reversible log. Web-only, in the Manage drawer.
function MoneySpots() {
  const [spots, setSpots] = useState(null);
  const [q, setQ] = useState('');
  const [banks, setBanks] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [places, setPlaces] = useState([]);
  const [act, setAct] = useState(null);   // { id, mode: 'receive'|'spend'|'logs' }
  const [logs, setLogs] = useState([]);
  const [f, setF] = useState({ src: 'person' });

  const load = useCallback(() => {
    api('/budget/money-spots/').then((x) => setSpots(Array.isArray(x) ? x : [])).catch(() => setSpots([]));
    api('/budget/banks/').then((b) => setBanks((b || []).filter((x) => !x.archived))).catch(() => {});
    api('/budget/places/').then((p) => setPlaces(p || [])).catch(() => {});
    api('/budget/items/').then((it) => {
      const seen = new Set(); const ex = [];
      (it || []).filter((i) => i.kind === 'expense').forEach((i) => { if (!seen.has(i.title)) { seen.add(i.title); ex.push(i); } });
      setExpenses(ex);
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addSpot = async () => {
    const name = await ask({ title: 'New money spot', message: 'Wallet, safe, envelope…', input: true, ok: 'Add' });
    if (!name || !name.trim()) return;
    await api('/budget/money-spots/', { method: 'POST', body: { name: name.trim() } }).catch(() => {});
    load();
  };
  const openAct = (id, mode) => {
    setF({ src: 'person' }); setLogs([]);
    setAct((a) => (a && a.id === id && a.mode === mode ? null : { id, mode }));
    if (mode === 'logs') api(`/budget/money-spots/${id}/movements/`).then((m) => setLogs(m || [])).catch(() => {});
  };
  const receive = async (id) => {
    const amount = Number(f.amount); if (!(amount > 0)) return;
    const body = f.src === 'bank' ? { amount, source: 'bank', bank: f.bank } : { amount, source: 'person', person: f.person || '' };
    if (f.src === 'bank' && !f.bank) return;
    await api(`/budget/money-spots/${id}/receive/`, { method: 'POST', body }).catch(() => {});
    setAct(null); load();
  };
  const spend = async (id) => {
    const amount = Number(f.amount); if (!(amount > 0)) return;
    await api(`/budget/money-spots/${id}/spend/`, { method: 'POST', body: { amount, item: f.item || null, place: f.place || null, note: f.note || '' } }).catch(() => {});
    setAct(null); load();
  };
  const delMv = async (mid, id) => {
    await api(`/budget/money-spot-movements/${mid}/`, { method: 'DELETE' }).catch(() => {});
    api(`/budget/money-spots/${id}/movements/`).then((m) => setLogs(m || [])).catch(() => {});
    load();
  };

  if (spots === null) return <div className="text-sm text-slate-400">Loading…</div>;
  const total = spots.reduce((sum, x) => sum + (Number(x.balance) || 0), 0);
  const ql = q.trim().toLowerCase();
  const shownSpots = ql ? spots.filter((x) => (x.name || '').toLowerCase().includes(ql)) : spots;
  return (
    <div data-guide="manage-cash">
      {/* Four-card summary strip (Pass 7) — real data from the spots list. */}
      {spots.length > 0 && (() => {
        const active = spots.filter((x) => !x.archived).length || spots.length;
        const received = spots.reduce((a, x) => a + Number(x.received || 0), 0);
        const spent = spots.reduce((a, x) => a + Number(x.spent || 0), 0);
        const cards = [
          ['Money spots', String(active), 'active'],
          ['Cash on hand', fmtR(total), 'across your spots'],
          ['Received', fmtR(received), 'in this month'],
          ['Spent', fmtR(spent), 'this month'],
        ];
        return (
          <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))' }}>
            {cards.map(([k, v, sub]) => (
              <div key={k} className="rounded-xl border border-edge bg-white/[0.03] px-4 py-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
                <div className="text-lg font-bold text-slate-100 truncate">{v}</div>
                <div className="text-[10px] text-slate-500 truncate">{sub}</div>
              </div>
            ))}
          </div>
        );
      })()}
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-bold">Cash on hand</h3>
        <span className="text-sm text-slate-400">{spots.length} spot{spots.length === 1 ? '' : 's'} · {fmtR(total)}</span>
      </div>
      <p className="text-xs text-slate-500 mb-3">Money you hold or stash — wallet, safe, envelope. Add it from a bank or from a person, then spend from it with where and which expense. Every move is a log you can reverse.</p>
      {spots.length > 0 && (
        <div className="mb-3">
          <SearchField value={q} onChange={setQ} placeholder="Search money spots…" />
        </div>
      )}
      {spots.length > 0 && (
        <button className="btn-primary !py-1.5 !px-3 text-sm mb-3" onClick={addSpot}>+ Add a money spot</button>
      )}
      {spots.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-5xl mb-3" style={{ opacity: 0.2 }}>🔒</div>
          <p className="text-sm text-slate-400">No cash spots yet — track a wallet, a safe or an envelope alongside your banks.</p>
          <button className="mt-4 btn-primary !py-2 !px-4 text-sm" onClick={addSpot}>Add a money spot</button>
        </div>
      ) : (
        <div className="grid gap-2">
          {shownSpots.map((sp) => (
            <div key={sp.id} className="rounded-xl border border-edge p-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{sp.name}</span>
                <span className="font-bold text-vblue">{fmtR(Number(sp.balance) || 0)}</span>
              </div>
              <div className="flex gap-4 mt-2 text-sm">
                <button className="text-emerald-400" onClick={() => openAct(sp.id, 'receive')}>+ Receive</button>
                <button className="text-vblue" onClick={() => openAct(sp.id, 'spend')}>− Spend</button>
                <button className="text-slate-400" onClick={() => openAct(sp.id, 'logs')}>Logs</button>
              </div>
              {act && act.id === sp.id && act.mode === 'receive' && (
                <div className="mt-3 grid gap-2">
                  <input className="input" placeholder="Amount (R)" inputMode="decimal" value={f.amount || ''} onChange={(e) => setF({ ...f, amount: e.target.value })} />
                  <div className="flex gap-2 text-xs">
                    <button className={`px-3 py-1.5 rounded-lg border ${f.src !== 'bank' ? 'border-vblue text-white' : 'border-edge text-slate-400'}`} onClick={() => setF({ ...f, src: 'person' })}>From a person</button>
                    <button className={`px-3 py-1.5 rounded-lg border ${f.src === 'bank' ? 'border-vblue text-white' : 'border-edge text-slate-400'}`} onClick={() => setF({ ...f, src: 'bank' })}>From a bank</button>
                  </div>
                  {f.src === 'bank' ? (
                    <select className="input" value={f.bank || ''} onChange={(e) => setF({ ...f, bank: e.target.value })}>
                      <option value="">Pick a bank…</option>
                      {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  ) : (
                    <input className="input" placeholder="Who from? (optional)" value={f.person || ''} onChange={(e) => setF({ ...f, person: e.target.value })} />
                  )}
                  <button className="btn-primary !py-1.5 text-sm" onClick={() => receive(sp.id)}>Add to {sp.name}</button>
                </div>
              )}
              {act && act.id === sp.id && act.mode === 'spend' && (
                <div className="mt-3 grid gap-2">
                  <input className="input" placeholder="Amount (R)" inputMode="decimal" value={f.amount || ''} onChange={(e) => setF({ ...f, amount: e.target.value })} />
                  <select className="input" value={f.item || ''} onChange={(e) => setF({ ...f, item: e.target.value })}>
                    <option value="">Which expense? (optional)</option>
                    {expenses.map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
                  </select>
                  <select className="input" value={f.place || ''} onChange={(e) => setF({ ...f, place: e.target.value })}>
                    <option value="">Where? (optional)</option>
                    {places.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <input className="input" placeholder="Note (optional)" value={f.note || ''} onChange={(e) => setF({ ...f, note: e.target.value })} />
                  <button className="btn-primary !py-1.5 text-sm" onClick={() => spend(sp.id)}>Spend from {sp.name}</button>
                </div>
              )}
              {act && act.id === sp.id && act.mode === 'logs' && (
                <div className="mt-3 grid gap-1.5">
                  {logs.length === 0 ? <p className="text-xs text-slate-500">No movements yet.</p> : logs.map((m) => (
                    <div key={m.id} className="flex items-center justify-between text-xs border-b border-edge/40 pb-1">
                      <span className="text-slate-300 min-w-0 truncate">{m.kind_display}{m.person ? ` · ${m.person}` : ''}{m.bank_name ? ` · ${m.bank_name}` : ''}{m.item_title ? ` · ${m.item_title}` : ''}{m.place_name ? ` · ${m.place_name}` : ''}<span className="text-slate-500"> · {String(m.on || '').slice(0, 10)}</span></span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className={m.kind === 'spend' || m.kind === 'to_bank' ? 'text-slate-400' : 'text-emerald-400'}>{fmtR(Number(m.amount) || 0)}</span>
                        <button className="text-slate-500 hover:text-red-400" onClick={() => delMv(m.id, sp.id)}>✕</button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
